import { createContext, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useGetUserProfile } from 'hooks/queries/use-get-user-profile.hook';
import { usePatientPortalContext } from 'contexts/patient-portal-provider.context';
import {
  getOnboardingStepFlags,
  OnboardingStepFlags,
} from '../onboarding-account-setup.selectors';

type UserProfileData = ReturnType<typeof useGetUserProfile>['data'];

export interface OnboardingProfileContextValue {
  profileData: UserProfileData;
  stepFlags: OnboardingStepFlags;
}

const OnboardingProfileContext =
  createContext<OnboardingProfileContextValue | null>(null);

/**
 *
 * Onboarding account setup provider that acts as the single SOT for the user profile data and step flags for all steps in the account setup flow.
 */
export const OnboardingProfileProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  // set refetchOnMount to false to avoid refetching user profile on mount
  // ^^ without this the component briefly re-renders at the first step
  const { data } = useGetUserProfile({ refetchOnMount: false });
  const { systemId } = usePatientPortalContext();

  // Steps are decided once from the profile data available when
  // onboarding starts. Completing a step (e.g. Terms) writes back to
  // this same profile and invalidates the query, so recomputing flags
  // from live `data` would hide/reshape steps out from under a Stepper
  // that's already mid-flow.
  const [initialData, setInitialData] = useState<UserProfileData>();
  if (initialData === undefined && data !== undefined) {
    setInitialData(data);
  }

  const stepFlags = useMemo(
    () => getOnboardingStepFlags(initialData, systemId),
    [initialData, systemId],
  );

  const value = useMemo<OnboardingProfileContextValue>(
    () => ({ profileData: data, stepFlags }),
    [data, stepFlags],
  );

  return (
    <OnboardingProfileContext.Provider value={value}>
      {children}
    </OnboardingProfileContext.Provider>
  );
};

export const useOnboardingProfile = (): OnboardingProfileContextValue => {
  const ctx = useContext(OnboardingProfileContext);
  if (!ctx) {
    throw new Error(
      'useOnboardingProfile must be used within an OnboardingProfileProvider',
    );
  }
  return ctx;
};
